import { oddsPageAtom, pageSideMatchIdAtom } from '@/atoms/odds-page';
import { Icon } from '@/components/icons/svg-icons'
import { useAccountInfo } from '@/hooks/query/account';
import { getEsportPreMatchUrl } from '@/hooks/query/use-esport-streaming-data';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { getGameInfoUrl } from '@/hooks/query/use-streaming-data';
import { modalService } from '@/services/modal.service';
import { streamingService } from '@/services/streaming.service';
import { useAppearanceStore } from '@/store/appearance';
import { useStreamingStore } from '@/store/streaming';
import { ViewEnum } from '@/types';
import { getMatchIdFromProviderList } from '@/utils/match-util';
import { getHomeTeamName, getLeagueName, getTeamName } from '@/utils/odds-utils';
import { MatchModelUnion, SportMatchModel } from '@nf/types/odds'
import { Button } from '@nf/ui'
import { AccountActStatus, KafkaNote } from '@nf/utils-common/constants';
import { matchEquals } from '@nf/utils-common/fpw/monadic';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { useParams, useRouter } from 'next/navigation';
import { isDesktop } from 'react-device-detect';
import { css } from 'styled-system/css';
import { useAnalyseAttribute } from '@/features/next-analyse';

interface StatisticButtonProps {
	match: SportMatchModel;
	isNeedSetMatchId: boolean;
	home?: string;
	away?: string;
	isShowText?: string;
	active?: boolean;
	onClick?: () => void;
}

export const StatisticButton = (statisticButtonProps: StatisticButtonProps) => {
	const router = useRouter();
	const { SiteConfigsData } = useGetSiteConfigs();
	const oddsData = useAtomValue(oddsPageAtom);
	const { user } = useAccountInfo();
	const { match, isNeedSetMatchId, isShowText, active, onClick } = statisticButtonProps;
	const { hasSportsStatistic, hasDecimalWidgets, hasEsportSportsStatistic } = streamingService.getVideoOptions(match as MatchModelUnion, false, SiteConfigsData, user);
	const setPageSideMatchId = useSetAtom(pageSideMatchIdAtom);
	const t = useTranslations('General');
	const [setStreamingStatus] = useStreamingStore(
		state => [
			state.setStreamingStatus
		],
	);
	const view = useAppearanceStore(state => state.view);
	const params = useParams();
	const isSingleMatchPage = params.matchId !== undefined;
	const taggedElement = useAnalyseAttribute(isSingleMatchPage ? 'use-statistic-info-from-single-match' : 'use-statistic-info');

	const hasStatisticInfoPermission = () => {
		if (!match.GameID) return false;

		switch (match.GameID) {
			case 43: // esports
				return hasEsportSportsStatistic;
			case 50: // cricket
				return hasDecimalWidgets;
			default:
				const providerMatchId = getMatchIdFromProviderList(match, '201');
				return hasSportsStatistic && !!providerMatchId;
		}
	}

	const openStatisticInfo = (e: { stopPropagation: () => void; }) => {
		e.stopPropagation();
		setKafka();

		if (user?.ActStatus !== AccountActStatus.Deposited) {
			modalService.openMessageModal(t('logineMsg1'));
			return;
		}
		if (isNeedSetMatchId && isDesktop) {
			setPageSideMatchId(match?.MatchId);
		}

		onClick && onClick();

		switch (match.GameID) {
			case 43: // esports
				const req = {
					homeName: getHomeTeamName(match?.TeamId1, false, '', oddsData?.teamNameMap),
					awayName: getTeamName(match.TeamId2, oddsData?.teamNameMap),
					matchid: match?.MatchId,
					leagueName: getLeagueName(match.LeagueId, oddsData?.leagueNameMap)
				};
				getEsportPreMatchUrl(req).then(res => {
					const url = res.data.Data;
					if (url) {
						window.open(url);
					}
				});
				break;
			case 50: // cricket
				setStreamingStatus('StaticInfo', '', []);
				const singleMatchHref = matchEquals(match?.MaT)
					.on('l', () => `/live/${match?.GameID}/${match?.LeagueId}/${match?.MatchId}`)
					.otherwise(() => `/sports/${match?.GameID}/${match?.MaT}/${match?.LeagueId}/${match?.MatchId}`);
				if (!isDesktop) {
					router.push(singleMatchHref);
				}
				break;
			default:
				const providerMatchId = getMatchIdFromProviderList(match, '201');
				getGameInfoUrl(providerMatchId).then(res => {
					const url = res.data.Data;
					if (url) {
						window.open(url);
					}
				});
				break;
		}
	};

	const setKafka = () => {
		if (view === ViewEnum.mars) {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: 6,
				action: isSingleMatchPage ? 24 : 6
			}
			setKafkaNormal(kafkaData);
		}
	}

	return (
		hasStatisticInfoPermission() ? (
			<Button
				variant="icon"
				className={style.btn}
				onClick={openStatisticInfo} data-active={active}
				ref={taggedElement}
			>
				<Icon name="statistic" />
				{isShowText ? (
					<span className="btn__text">{t('lbl_StatsInfo')}</span>
				) : null}
			</Button>
		) : null
	)
}

const style = {
	btn: css({
		_active: {
			'& .icon': {
				_galaxy: { color: 'var(--p500)' },
				_mars: { color: 'primary.300' }
			},
		}
	})
}